/**
 * Validación y formateo de teléfonos según ficha técnica DGI Panamá v1.00
 * Campo B309 (dTfnEm) y B408 (dTfnRec)
 *
 * Formatos finales aceptados por DGI:
 *   - 999-9999         (7 dígitos, fijo Panamá)
 *   - 9999-9999        (8 dígitos, celular Panamá)
 *   - 999999-999-9999  (13 dígitos, internacional + fijo)
 *   - 999999-9999-9999 (14 dígitos, internacional + celular)
 *
 * El usuario puede ingresar el número con +código de país y el sistema
 * lo convierte automáticamente al formato DGI (rellena el código con
 * ceros hasta 6 dígitos).
 */

const PHONE_FORMAT_HELP =
  'Panamá: 7 u 8 dígitos sin +507. Internacional: +código número (ej: +1 555-1234)';

interface ParsedPhone {
  countryCode: string | null;
  localDigits: string;
}

/**
 * Separa el código de país del número local. Reconoce inputs como:
 *   +1 555-1234       → { countryCode: '1', localDigits: '5551234' }
 *   +52 5512-3456     → { countryCode: '52', localDigits: '55123456' }
 *   +507 6789-1234    → { countryCode: null, localDigits: '67891234' } (Panamá → local)
 *   6789-1234         → { countryCode: null, localDigits: '67891234' }
 *   000001-555-1234   → { countryCode: null, localDigits: '0000015551234' }
 */
const parsePhoneInput = (phone: string): ParsedPhone => {
  const trimmed = phone.trim();

  if (trimmed.startsWith('+')) {
    const rest = trimmed.substring(1).trimStart();
    const allDigits = rest.replace(/\D/g, '');

    // +507XXXXXXXX o +507 XXXX-XXXX → Panamá local (sin importar separadores)
    if (allDigits.startsWith('507') && (allDigits.length === 10 || allDigits.length === 11)) {
      return { countryCode: null, localDigits: allDigits.substring(3) };
    }

    // Código de país + separador (espacio/guion/paréntesis) + número
    const match = rest.match(/^(\d{1,4})[\s\-().]+(.+)$/);
    if (match) {
      const countryCode = match[1];
      const localDigits = match[2].replace(/\D/g, '');
      if (countryCode === '507') {
        return { countryCode: null, localDigits };
      }
      return { countryCode, localDigits };
    }

    // Sin separador → mejor esfuerzo: tratamos todos los dígitos como locales
    return { countryCode: null, localDigits: allDigits };
  }

  let digits = trimmed.replace(/\D/g, '');
  if (digits.startsWith('507') && (digits.length === 10 || digits.length === 11)) {
    digits = digits.substring(3);
  }
  return { countryCode: null, localDigits: digits };
};

/**
 * Formatea un teléfono a uno de los 4 formatos aceptados por DGI.
 * Devuelve null si el número no encaja en ninguno.
 *
 * Si el input trae +código, lo rellena con ceros a la izquierda hasta 6
 * dígitos automáticamente.
 */
export const formatPhoneDGI = (phone: string | null | undefined): string | null => {
  if (!phone) return null;

  const { countryCode, localDigits } = parsePhoneInput(phone);
  if (!localDigits) return null;

  if (countryCode) {
    if (countryCode.length > 6) return null;

    if (localDigits.length === 7) {
      const padded = countryCode.padStart(6, '0');
      return `${padded}-${localDigits.substring(0, 3)}-${localDigits.substring(3)}`;
    }
    if (localDigits.length === 8) {
      const padded = countryCode.padStart(6, '0');
      return `${padded}-${localDigits.substring(0, 4)}-${localDigits.substring(4)}`;
    }
    if (localDigits.length > 8) {
      const total = countryCode + localDigits;
      if (total.length <= 13) {
        const padded = total.padStart(13, '0');
        return `${padded.substring(0, 6)}-${padded.substring(6, 9)}-${padded.substring(9)}`;
      }
      if (total.length === 14) {
        return `${total.substring(0, 6)}-${total.substring(6, 10)}-${total.substring(10)}`;
      }
      return null;
    }
    return null;
  }

  switch (localDigits.length) {
    case 7:
      return `${localDigits.substring(0, 3)}-${localDigits.substring(3)}`;
    case 8:
      return `${localDigits.substring(0, 4)}-${localDigits.substring(4)}`;
    case 13:
      return `${localDigits.substring(0, 6)}-${localDigits.substring(6, 9)}-${localDigits.substring(9)}`;
    case 14:
      return `${localDigits.substring(0, 6)}-${localDigits.substring(6, 10)}-${localDigits.substring(10)}`;
    default:
      return null;
  }
};

export interface PhoneValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Valida que el teléfono cumpla con uno de los 4 formatos DGI.
 * Mensajes pensados para guiar al usuario, no para acusarlo.
 */
export const validatePhoneDGI = (
  phone: string | null | undefined,
  options: { required?: boolean } = {},
): PhoneValidationResult => {
  const { required = false } = options;

  if (!phone || !phone.trim()) {
    return required
      ? { valid: false, error: 'El teléfono es requerido' }
      : { valid: true };
  }

  if (!/^[\d\s\-+()]+$/.test(phone)) {
    return {
      valid: false,
      error: 'Solo se permiten números, guiones, espacios y el signo +',
    };
  }

  if (formatPhoneDGI(phone)) {
    return { valid: true };
  }

  const { countryCode, localDigits } = parsePhoneInput(phone);

  if (countryCode) {
    if (countryCode.length > 6) {
      return {
        valid: false,
        error: `Código de país muy largo (${countryCode.length} dígitos). Máximo 6.`,
      };
    }
    if (localDigits.length < 7) {
      return {
        valid: false,
        error: `Faltan dígitos en el número (tienes ${localDigits.length}).`,
      };
    }
    const total = countryCode.length + localDigits.length;
    if (total > 14) {
      return {
        valid: false,
        error: `Número muy largo (${total} dígitos en total). Máximo permitido: 14.`,
      };
    }
    return { valid: false, error: 'Formato inválido' };
  }

  if (localDigits.length < 7) {
    return {
      valid: false,
      error: `Faltan dígitos (tienes ${localDigits.length}). ${PHONE_FORMAT_HELP}`,
    };
  }
  if (localDigits.length > 14) {
    return {
      valid: false,
      error: `Sobran dígitos (tienes ${localDigits.length}). Máximo 8 para Panamá, o usa +código de país.`,
    };
  }
  return {
    valid: false,
    error: `Longitud inválida (${localDigits.length} dígitos). ${PHONE_FORMAT_HELP}`,
  };
};

/**
 * Formato amigable para mostrar al usuario.
 * - Panamá: XXX-XXXX o XXXX-XXXX
 * - Internacional: "+CC XXXX-XXXX" (sin rellenar ceros)
 *
 * Devuelve el input original si no encaja en ningún patrón conocido.
 */
export const formatPhoneDisplay = (phone: string | null | undefined): string => {
  if (!phone) return '';

  const { countryCode, localDigits } = parsePhoneInput(phone);
  if (!localDigits) return phone;

  const formatLocal = (digits: string): string | null => {
    if (digits.length === 7) return `${digits.substring(0, 3)}-${digits.substring(3)}`;
    if (digits.length === 8) return `${digits.substring(0, 4)}-${digits.substring(4)}`;
    if (digits.length > 8) {
      const last4 = digits.slice(-4);
      const middle = digits.slice(0, -4);
      return `${middle}-${last4}`;
    }
    return null;
  };

  if (countryCode) {
    const local = formatLocal(localDigits);
    if (!local) return phone;
    return `+${countryCode} ${local}`;
  }

  const local = formatLocal(localDigits);
  return local ?? phone;
};

/**
 * Texto de ayuda recomendado para mostrar bajo el input.
 */
export const PHONE_HELPER_TEXT = PHONE_FORMAT_HELP;
